import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * Trial-status banner — appears in the AppShell header when the user has
 * 7 or fewer trial days left, with a one-tap upgrade CTA. Audit finding:
 * the platform was deferring all conversion messaging until the day-15 hard
 * cliff, which leaves money on the table. A 7-day-warning banner is the
 * cheapest soft-paywall touchpoint.
 *
 * (The old early-trial Founding Member $99-lifetime banner was retired at
 * public launch 2026-07-13 — too much value given away against $20/mo.)
 */
export function TrialBanner() {
  const { isTrialing, trialDaysLeft, subscription } = useSubscription();
  if (!isTrialing) return null;
  if (trialDaysLeft > 7) return null;

  // Tone shifts as the trial gets closer to expiry.
  const urgent = trialDaysLeft <= 2;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`px-3 py-2 text-xs font-semibold flex items-center justify-center gap-2 border-b
        ${urgent
          ? 'bg-rose-100 dark:bg-rose-950/50 text-rose-800 dark:text-rose-200 border-rose-200 dark:border-rose-800'
          : 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800'}`}
    >
      <Sparkles className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span>
        {trialDaysLeft === 0
          ? 'Your trial ends today'
          : trialDaysLeft === 1
            ? 'Your trial ends tomorrow'
            : `${trialDaysLeft} days left in your trial`}
      </span>
      <Link
        to={subscription?.stripe_subscription_id ? '/pricing' : `/pricing?from=trial-banner&days=${trialDaysLeft}`}
        className="ml-1 px-2.5 py-1 rounded-full font-bold text-[11px]
          bg-white dark:bg-gray-900 hover:opacity-90 transition active:scale-95"
      >
        {subscription?.stripe_subscription_id ? 'Manage billing →' : 'Upgrade →'}
      </Link>
    </div>
  );
}
